/* Kocktail hero — scroll-docking wordmark.
   The large "kocktail" mark rests in the hero and travels into the header
   logo slot as the page scrolls (rotating flat if it rests vertical).

   DETERMINISM CONTRACT: every rendered frame is a pure function of the
   current scroll offset. There is NO cached anchor, no snapshot taken "at
   rest", no self-heal — all of those made the rest position depend on WHEN
   it was measured (page load, logo image load, font load, resize, retry
   timers), so scrolling back up landed the mark somewhere different each
   time. Instead, each frame:

     1. p = clamp01(scrollY / TRAVEL)             — progress from absolute scroll
     2. rest position computed fresh, in DOCUMENT coordinates
        (rect.top + scrollY — scroll-invariant for a stable layout)
     3. rendered = lerp(rest - scrollY, dockTarget, p)

   Same scroll offset → same frame, forward or reverse, always. Layout
   changes (image/font loads, editor re-renders) are simply picked up on
   the next frame because nothing is remembered between frames. */
(function () {
  function lerp(a, b, p) { return a + (b - a) * p; }
  function clamp01(v) { return Math.min(1, Math.max(0, v)); }
  function isMobile() { return window.matchMedia('(max-width: 749px)').matches; }

  var running = false;

  function hero() { return document.querySelector('.kx-hero'); }
  function logo() { return document.getElementById('kx-herologo'); }

  // This theme scrolls an inner container (.page-wrapper), not the window, so
  // window.scrollY is always 0. Find the real scroll container by walking up
  // from the hero; fall back to the window.
  var scrollerCache = null;
  function isScroller(el) {
    var oy = window.getComputedStyle(el).overflowY;
    return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2;
  }
  // WHICH element scrolls is a function of viewport width, not a fact about the
  // page. This theme scrolls an inner container at desktop widths and hands the
  // job back to the window at mobile ones, so a container cached on load stops
  // being the scroller the moment the window is resized past the breakpoint.
  // It stays in the DOM, so an isConnected check does not notice: the cache
  // kept answering with an element whose scrollTop was permanently 0, the mark
  // read the page as never scrolled, and it never docked. Resizing INTO mobile
  // broke it; loading straight into mobile did not.
  function forgetScroller() { scrollerCache = null; }
  function scrollEl() {
    if (scrollerCache && scrollerCache.isConnected && isScroller(scrollerCache)) return scrollerCache;
    scrollerCache = null;
    var el = hero();
    while (el && el !== document.body && el !== document.documentElement) {
      if (isScroller(el)) { scrollerCache = el; return el; }
      el = el.parentElement;
    }
    return null;
  }
  // Belt and braces: whichever of the two has actually moved is the one to
  // believe. If a resize is ever missed, or the theme swaps containers without
  // one, the mark still tracks the scroll instead of freezing at the top.
  function pageScroll() {
    var win = window.scrollY || window.pageYOffset || 0;
    var se = scrollEl();
    return se ? Math.max(se.scrollTop, win) : win;
  }

  // Keep exactly ONE wordmark, parked on <body> (so `position: fixed` is
  // viewport-relative, never trapped by the hero's overflow/perspective).
  // When Shopify re-renders the hero in the editor it emits a fresh wordmark
  // inside the section; reconcile de-dupes and re-homes it so we never end up
  // with a stale copy stranded at its CSS default position over the page.
  function reconcileLogo() {
    var marks = document.querySelectorAll('.kx-herologo');
    if (!marks.length) return null;
    var keep = null;
    marks.forEach(function (m) {
      if (m.closest('.kx-hero-section, .shopify-section')) keep = m;
    });
    if (!keep) keep = marks[marks.length - 1];
    marks.forEach(function (m) { if (m !== keep) m.remove(); });
    if (keep.parentElement !== document.body) document.body.appendChild(keep);
    return keep;
  }

  // The floating wordmark is a fixed element; it must not sit on top of an open
  // menu / cart / search overlay.
  function overlayOpen() {
    return !!document.querySelector(
      'header-drawer details[open], .menu-drawer[open], cart-drawer[open], dialog[open]'
    );
  }

  // The flying wordmark IS the logo while it is visible, so the header's own
  // logo hides meanwhile — and returns the moment the wordmark is not visible,
  // so the header is never left blank. Idempotent; re-applied every frame so it
  // survives header re-renders (cart-badge morph, transparent toggle).
  //
  // It also flags the state on the header itself. With the logo out in the hero
  // the header's top row is an empty band with the search on one side and the
  // cart on the other, floating above the menu line — which is what the header
  // read as before. `kx-header-onerow` lets the CSS collapse the two rows onto
  // one line for exactly as long as the logo is away; when the mark docks
  // ("the logo goes up") the class comes off and the two-row header returns.
  function setHeaderLogoHidden(hide) {
    var hdr = document.getElementById('header-component');
    if (hdr) hdr.classList.toggle('kx-header-onerow', !!hide);

    var logos = document.querySelectorAll('.header-logo');
    if (!logos.length) return;
    logos.forEach(function (h) {
      var isHidden = h.style.visibility === 'hidden';
      if (hide && !isHidden) {
        h.style.opacity = '0';
        h.style.visibility = 'hidden';
        h.style.pointerEvents = 'none';
      } else if (!hide && isHidden) {
        h.style.opacity = '';
        h.style.visibility = '';
        h.style.pointerEvents = '';
      }
    });
  }

  // Exactly ONE logo is visible at any moment, and the header's own logo is the
  // fail-safe. Below the hand-off point the flying wordmark IS the logo; at or
  // past it, the wordmark steps aside and the real header logo takes over for
  // the rest of the page. The wordmark appears INSTANTLY and only fades out —
  // a fade-in opened a window where neither logo was visible.
  var HANDOFF = 0.92;
  function applyLogoVisibility(el, progress, headerHidden) {
    var on = progress < HANDOFF && !headerHidden;
    el.style.transition = on ? 'none' : 'opacity 0.28s ease';
    el.style.opacity = on ? '1' : '0';
    el.style.pointerEvents = on ? 'auto' : 'none';
    setHeaderLogoHidden(on);
  }

  // Where the wordmark docks, in viewport coordinates. The header is sticky, so
  // its box is viewport-stable. On MOBILE the header logo cell mis-measures on
  // small screens, so dock to the true centre and only take Y from the box.
  function dockTarget() {
    var logos = document.querySelectorAll('.header-logo');
    var best = null;
    for (var i = 0; i < logos.length; i++) {
      var r = logos[i].getBoundingClientRect();
      if (r.height > 0 && r.top >= 0 && r.top < 200) { best = r; break; }
    }
    if (isMobile()) {
      return { x: window.innerWidth / 2, y: best ? best.top + best.height / 2 : 44 };
    }
    if (best) return { x: best.left + best.width / 2, y: best.top + best.height / 2 };
    return { x: window.innerWidth / 2, y: 36 };
  }

  // Rest position of the mark, computed fresh from the CURRENT layout, returned
  // in DOCUMENT coordinates (viewport rect + current scroll). Document
  // coordinates are invariant under scroll, so this returns the same numbers no
  // matter when it is called — which is the whole determinism guarantee.
  function restPosition(el, h, sy, halfH, halfW, mobile) {
    var pos = el.getAttribute('data-kx-logo-pos') || 'middle_right';
    var inset = mobile ? 16 : 28;
    var edge = mobile ? 34 : Math.round(window.innerWidth * 0.1);
    var hr = h.getBoundingClientRect();
    var title = document.getElementById('kx-hero-title');
    var t = title ? title.getBoundingClientRect() : null;
    var x, y;

    if (pos === 'above_heading' && t) {
      // In the copy flow, directly above the H1, matching the copy's own
      // horizontal alignment. CSS reserves the space so it cannot overlap.
      var content = h.querySelector('.kx-hero__content');
      var align = content ? window.getComputedStyle(content).textAlign : 'center';
      if (align === 'left' || align === 'start') x = t.left + halfW;
      else if (align === 'right' || align === 'end') x = t.right - halfW;
      else x = t.left + t.width / 2;
      return { x: x, y: t.top + sy - inset - halfH };
    }

    // Split layout: the mark lives over the MEDIA column, on the chosen side of
    // it, so it never sits over the copy panel.
    var media = h.classList.contains('kx-hero--split') ? h.querySelector('.kx-hero__bg') : null;
    var mr = media ? media.getBoundingClientRect() : null;
    if (mr && mr.width > 0) {
      var wantsLeft = pos.indexOf('left') !== -1;
      x = wantsLeft ? (mr.left + inset + halfW) : (mr.right - inset - halfW);
      switch (pos) {
        case 'top_right':
        case 'top_left':     y = mr.top + halfH + inset; break;
        case 'bottom_right': y = mr.bottom - halfH - inset; break;
        default:             y = mr.top + mr.height / 2;
      }
      y = Math.max(mr.top + halfH + inset, Math.min(y, mr.bottom - halfH - inset));
      return { x: x, y: y + sy };
    }

    switch (pos) {
      case 'top_right':
        x = window.innerWidth - edge; y = hr.top + halfH + inset; break;
      case 'top_left':
        x = edge;                     y = hr.top + halfH + inset; break;
      case 'bottom_right':
        x = window.innerWidth - edge; y = hr.bottom - halfH - inset; break;
      default: // middle_right — level with the headline
        x = mobile ? (window.innerWidth - 34) : window.innerWidth * 0.9;
        y = t ? (t.top + t.height / 2) : window.innerHeight * 0.5;
    }
    return { x: x, y: y + sy };
  }

  // Add ?kxlogo=1 to the URL to print the numbers this file is working from.
  var DEBUG = /[?&]kxlogo=1/.test(window.location.search);
  var lastDebug = '';
  function debugDump(el, sy, p, rest, d) {
    if (!DEBUG || !el) return;
    var img = el.querySelector('img');
    var line = [
      'scroll ' + Math.round(sy),
      'progress ' + p.toFixed(2),
      'rest(doc) ' + Math.round(rest.x) + ',' + Math.round(rest.y),
      'dock ' + Math.round(d.x) + ',' + Math.round(d.y),
      'mark box ' + el.offsetWidth + 'x' + el.offsetHeight,
      'img ' + (img ? (img.naturalWidth + 'x' + img.naturalHeight + ' complete=' + img.complete) : 'none (drawn text)'),
      'viewport ' + window.innerWidth + 'x' + window.innerHeight
    ].join(' | ');
    if (line !== lastDebug) { lastDebug = line; console.log('[kx logo]', line); }
  }

  function update() {
    var el = logo();
    var h = hero();
    // No wordmark element: the header keeps its own logo (never blank).
    if (!el) { setHeaderLogoHidden(false); return; }
    // No hero (removed / editor mid-swap): hide the fixed mark, restore the
    // header's own logo.
    if (!h) { el.style.opacity = '0'; el.style.pointerEvents = 'none'; setHeaderLogoHidden(false); return; }
    // Never float over an open drawer/modal.
    if (overlayOpen()) { el.style.opacity = '0'; el.style.pointerEvents = 'none'; setHeaderLogoHidden(false); return; }

    var mobile = isMobile();
    var base = mobile
      ? (parseFloat(el.getAttribute('data-kx-logo-size-m')) || 30)
      : (parseFloat(el.getAttribute('data-kx-logo-size')) || 104);
    // Docked size follows Theme settings > Logo height, so the hand-off to the
    // real header logo is at matching size.
    var end = mobile
      ? (parseFloat(el.getAttribute('data-kx-logo-dock-m')) || 26)
      : (parseFloat(el.getAttribute('data-kx-logo-dock')) || 30);
    var startRot = parseFloat(el.getAttribute('data-kx-logo-rot'));
    if (isNaN(startRot)) startRot = -90;
    var vertical = startRot !== 0;

    // Size before measuring: the box is what everything else is derived from.
    el.style.display = '';
    el.style.fontSize = base + 'px';
    var boxW = el.offsetWidth || (mobile ? base * 4 : 420);
    var boxH = el.offsetHeight || (mobile ? base * 1.3 : 130);
    // Rotated -90deg, the mark's on-screen HEIGHT is its unrotated width and
    // its WIDTH is its unrotated height. Upright, they are as measured.
    var halfH = (vertical ? boxW : boxH) / 2;
    var halfW = (vertical ? boxH : boxW) / 2;

    // ---- The pure-function core ------------------------------------------
    var sy = pageScroll();
    var TRAVEL = mobile ? 200 : 420;
    var p = clamp01(sy / TRAVEL);

    var rest = restPosition(el, h, sy, halfH, halfW, mobile); // document coords
    var d = dockTarget();                                     // viewport coords

    var x = lerp(rest.x, d.x, p);           // horizontal: doc == viewport
    var y = lerp(rest.y - sy, d.y, p);      // vertical: convert doc -> viewport
    var size = lerp(base, end, p);
    var rot = lerp(startRot, 0, p);
    // ----------------------------------------------------------------------

    el.style.left = x + 'px';
    el.style.top = y + 'px';
    el.style.transform =
      'translate(-50%, -50%) rotate(' + rot + 'deg) scale(' + (size / base) + ')';
    el.style.filter = p < 0.6 ? 'drop-shadow(0 22px 32px rgba(20,32,43,0.24))' : 'none';

    var hdr = document.getElementById('header-component');
    var hidden = hdr && hdr.classList.contains('kx-header-hidden');
    applyLogoVisibility(el, p, hidden);
    debugDump(el, sy, p, rest, d);
  }

  var lastSy = null;
  var idleFrames = 0;
  function loop() {
    update();
    if (!hero()) { running = false; return; }
    // Park after ~0.75s of stable scroll. The parked frame is already correct
    // for the current offset (each frame is a pure function of scroll), so
    // parking cannot strand the mark: iOS momentum keeps scroll CHANGING, which
    // keeps the loop alive, and every wake signal below re-arms it. Idle cost
    // drops to zero instead of layout reads at 60fps forever.
    var sy = pageScroll();
    if (sy === lastSy) { idleFrames++; } else { idleFrames = 0; lastSy = sy; }
    if (idleFrames > 45) { running = false; return; }
    requestAnimationFrame(loop);
  }

  function kick() {
    if (running) return;
    running = true;
    requestAnimationFrame(loop);
  }

  function init() {
    var el = reconcileLogo();
    if (!el) return;
    el.style.opacity = '1';

    update(); // position before first paint so there's no "no logo" flash
    kick();

    ['scroll', 'wheel', 'touchmove'].forEach(function (ev) {
      window.addEventListener(ev, kick, { passive: true, capture: true });
    });
    // Re-measure AND re-decide what scrolls: a width change can move the
    // breakpoint under us.
    window.addEventListener('resize', function () { forgetScroller(); kick(); });
    window.addEventListener('orientationchange', function () { forgetScroller(); kick(); });
    if (window.matchMedia) {
      var mq = window.matchMedia('(max-width: 749px)');
      var onBreak = function () { forgetScroller(); kick(); };
      if (mq.addEventListener) mq.addEventListener('change', onBreak);
      else if (mq.addListener) mq.addListener(onBreak);
    }
    window.addEventListener('load', kick);
    // Any subresource finishing (the logo image, hero photo, fonts) can move
    // the layout the rest position is computed from — wake for one pass.
    document.addEventListener('load', kick, true);
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(kick); }
    // The header hiding/revealing changes the mark's visibility; watch the
    // class rather than polling for it.
    var hdrEl = document.getElementById('header-component');
    if (hdrEl && window.MutationObserver) {
      new MutationObserver(kick).observe(hdrEl, { attributes: true, attributeFilter: ['class'] });
    }

    // Theme editor: Shopify replaces the hero (and re-emits the wordmark) on
    // every edit. Re-home the mark and re-arm the loop.
    document.addEventListener('shopify:section:load', function () {
      reconcileLogo(); kick();
    });
    document.addEventListener('shopify:section:unload', function () {
      reconcileLogo(); kick();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
